// exp_PORTFOLIO-61587_file
import { FC, useState } from 'react';

import { push } from 'connected-react-router';
import {
    CampaignCreationWizardStep,
    getCampaignCreationWizardVacancyOfTheDayProgress,
} from 'utils/campaignCreationWizardFlow/campaignCreationWizardFlow';
import { CREATE_MENU_PAGE_ROUTE, DASHBOARD_PAGE_ROUTE } from 'utils/constants/app';
import { CampaignProductType } from 'utils/constants/campaign';
import { NON_BREAKING_SPACE } from 'utils/constants/common';
import { getVacancyOfTheDayErrors, hasVacancyOfTheDayErrors, VacancyOfTheDayErrors } from 'utils/vacancyOfTheDay';

import vacancyOfTheDaySubmitButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/vacancy_of_the_day/vacancy_of_the_day_submit_button_click';
import { ActionBar, Button, ProgressBar, useBreakpoint } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';
import { useCampaignCreationWizardBackAlert } from 'hooks/useCampaignCreationWizardBackAlert';
import { useCampaignCreationWizardStepNavigation } from 'hooks/useCampaignCreationWizardStepNavigation';

import FormatMoney from 'components/FormatMoney';

import { hideVacancyOfTheDay } from 'components/VacancyOfTheDayBuilder/hideVacancyOfTheDay';
import { useVacancyOfTheDayAnalyticsParams } from 'components/VacancyOfTheDayBuilder/useVacancyOfTheDayAnalyticsParams';

import {
    selectVacancyOfTheDayAreas,
    selectVacancyOfTheDayBudget,
    selectVacancyOfTheDayClicks,
    selectVacancyOfTheDayPreset,
    selectVacancyOfTheDayPresets,
    selectVacancyOfTheDayRoles,
    selectVacancyOfTheDayVacancy,
    setVacancyOfTheDayErrors,
} from 'store/models/vacancyOfTheDay';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/CampaignCreationWizardFlow/CampaignCreationWizardProgress.module.less';

const ERROR_FIELD_NAMES: { key: keyof VacancyOfTheDayErrors; name: string }[] = [
    { key: 'areas', name: 'geo' },
    { key: 'roles', name: 'professional_role' },
    { key: 'clicks', name: 'clicks' },
    { key: 'budget', name: 'budget' },
];

const getVacancyOfTheDayErrorList = (errors: VacancyOfTheDayErrors): string | null => {
    const names = ERROR_FIELD_NAMES.filter(({ key }) => errors[key]).map(({ name }) => name);
    return names.length > 0 ? names.join(',') : null;
};

const TrlKeys = {
    toAds: 'CampaignCreationWizard.actionBar.toAds',
    back: 'CampaignCreationWizard.actionBar.back',
    start: 'VacancyOfTheDayPage.actionBar.start',
};

export const VacancyOfTheDayActions: FC = () => {
    const { vacancyId, vacancyName } = useSelector(selectVacancyOfTheDayVacancy);
    const areas = useSelector(selectVacancyOfTheDayAreas);
    const roles = useSelector(selectVacancyOfTheDayRoles);
    const preset = useSelector(selectVacancyOfTheDayPreset);
    const presets = useSelector(selectVacancyOfTheDayPresets);
    const clicks = useSelector(selectVacancyOfTheDayClicks);
    const budget = useSelector(selectVacancyOfTheDayBudget);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const dispatch = useDispatch();
    const trls = useTrls();
    const { isXS, isMobile } = useBreakpoint();
    const { backAlert, openBackAlert } = useCampaignCreationWizardBackAlert();
    const goToWizardStep = useCampaignCreationWizardStepNavigation();

    const isWizardFlowEnabled = Boolean(vacancyId);
    const { params } = useVacancyOfTheDayAnalyticsParams();

    const handleStart = () => {
        if (isSubmitting) {
            return;
        }

        const errors = getVacancyOfTheDayErrors({ areas, roles, preset, clicks, budget, presets });
        dispatch(setVacancyOfTheDayErrors(errors));
        vacancyOfTheDaySubmitButtonClick({
            ...params,
            errors: getVacancyOfTheDayErrorList(errors),
        });
        if (hasVacancyOfTheDayErrors(errors)) {
            return;
        }

        setIsSubmitting(true);
        void Promise.resolve(dispatch(hideVacancyOfTheDay())).finally(() => setIsSubmitting(false));
    };

    const handleToAdsClick = () => {
        const leave = () => dispatch(push(DASHBOARD_PAGE_ROUTE));
        if (isWizardFlowEnabled) {
            openBackAlert(leave);
            return;
        }
        leave();
    };

    const handleBackClick = () => {
        if (isWizardFlowEnabled) {
            openBackAlert(() => {
                goToWizardStep(CampaignCreationWizardStep.PromotionType, {
                    vacancyId: vacancyId ?? null,
                    vacancyName: vacancyName ?? undefined,
                    productType: CampaignProductType.VacancyOfTheDay,
                });
            });
            return;
        }
        dispatch(push(CREATE_MENU_PAGE_ROUTE, { saveMainMenuData: true }));
    };

    const primaryActions = (
        <>
            <Button mode="secondary" style="accent" onClick={handleBackClick} data-qa="vacancy-of-the-day-back">
                {trls(TrlKeys.back)}
            </Button>
            <Button
                mode="primary"
                style="accent"
                onClick={handleStart}
                loading={isSubmitting}
                data-qa="vacancy-of-the-day-start"
                postfix={<FormatMoney groupSeparator={NON_BREAKING_SPACE}>{budget}</FormatMoney>}
                spaceBetween={false}
            >
                {trls(TrlKeys.start)}
            </Button>
        </>
    );

    const secondaryActions = !isMobile && (
        <Button mode="secondary" style="accent" onClick={handleToAdsClick} data-qa="vacancy-of-the-day-to-ads">
            {trls(TrlKeys.toAds)}
        </Button>
    );

    if (isWizardFlowEnabled) {
        const progressConfig = getCampaignCreationWizardVacancyOfTheDayProgress();
        const progress = (
            <ProgressBar
                progress={progressConfig.progress}
                segments={progressConfig.segments}
                data-qa="campaign-creation-wizard-progress"
            />
        );

        return (
            <>
                <ActionBar
                    type={isXS ? 'vertical' : 'auto'}
                    isSticky
                    showProgress={progress}
                    additionalContent={isMobile ? <div className={styles.progress}>{progress}</div> : null}
                    primaryActions={primaryActions}
                    secondaryActions={secondaryActions}
                />
                {backAlert}
            </>
        );
    }

    return (
        <ActionBar
            type={isXS ? 'vertical' : 'auto'}
            isSticky
            showDivider="always"
            primaryActions={primaryActions}
            secondaryActions={secondaryActions}
        />
    );
};
